import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bitcoin } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { CaptchaField, useCaptcha } from "@/components/Captcha";

export function AuthShell({ title, subtitle, children, footer }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 border-r border-border bg-zinc-950/60">
        <div className="flex items-center gap-2.5">
          <span className="h-9 w-9 rounded-md bg-amber-500 grid place-items-center text-zinc-950"><Bitcoin size={20} strokeWidth={2.5} /></span>
          <span className="font-heading font-bold">BTC Gateway</span>
        </div>
        <div className="max-w-md">
          <div className="eyebrow mb-4">Passerelle non-custodiale</div>
          <h2 className="text-4xl font-bold tracking-tight leading-tight">Vos clients paient. Les bitcoins arrivent directement dans votre wallet.</h2>
          <p className="text-zinc-400 mt-4 text-sm leading-relaxed">Dérivation BIP84 depuis votre zpub, aucune clé privée côté serveur, webhooks signés HMAC, mode testnet et mainnet séparés.</p>
        </div>
        <div className="font-mono text-xs text-zinc-600">m/84'/0'/0'/0/i · mempool.space · AES-256-GCM</div>
      </div>
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm fade-up">
          <h1 className="text-2xl font-bold tracking-tight">{title}</h1>
          <p className="text-sm text-zinc-400 mt-1 mb-8">{subtitle}</p>
          {children}
          <div className="text-sm text-zinc-500 mt-6">{footer}</div>
        </div>
      </div>
    </div>
  );
}

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const { captcha, setCaptcha, reload, fields } = useCaptcha();

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const u = await login(form.email, form.password, fields);
      nav(u.role === "admin" ? "/admin" : "/app");
    } catch (e2) { setErr(errMsg(e2)); reload(); } finally { setBusy(false); }
  };

  return (
    <AuthShell title="Connexion" subtitle="Accédez à votre dashboard marchand ou super-admin." footer={<>Pas encore de compte ? <Link to="/register" className="text-amber-400 hover:underline" data-testid="goto-register-link">Créer un compte marchand</Link></>}>
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5"><Label>Email</Label><Input type="email" required maxLength={254} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="login-email-input" /></div>
        <div className="space-y-1.5"><Label>Mot de passe</Label><Input type="password" required maxLength={128} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="login-password-input" /></div>
        <CaptchaField captcha={captcha} setCaptcha={setCaptcha} reload={reload} prefix="login" />
        {err && <p className="text-sm text-red-400" data-testid="login-error">{err}</p>}
        <Button type="submit" disabled={busy} className="w-full bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold" data-testid="login-submit-button">{busy ? "Connexion…" : "Se connecter"}</Button>
        <div className="text-right"><Link to="/forgot-password" className="text-xs text-zinc-500 hover:text-amber-400 transition-colors" data-testid="forgot-password-link">Mot de passe oublié ?</Link></div>
      </form>
    </AuthShell>
  );
}
